import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserLogged } from '@/hooks/useUserLogged';
import { useAuthStore } from '@/state/authState';
import { listCodebaseRepos } from '@/apis/unifiedCodebaseManagement';
import {
  listObservabilityAudits,
  getObservabilityAuditStatus,
  getObservabilityAuditAssessment,
  getObservabilityCoverageHeatmap,
  getObservabilityAuditIssues,
  getObservabilityHighLevelRecs,
  startObservabilityAudit,
  autoFixObservabilityIssue,
  cancelObservabilityAudit,
  getGroupedIssues,
  regenerateObservabilitySummary,
  dismissObservabilityIssues,
  dismissObservabilityRecommendation,
  type AuditSummary,
  type ListAuditsResponse,
  type GetAuditStatusResponse,
  type GetAuditAssessmentResponse,
  type GetCoverageHeatmapResponse,
  type GetAuditIssuesResponse,
  type GetHighLevelRecsResponse,
  type StartAuditRequest,
  type StartAuditResponse,
  type AutoFixIssueRequest,
  type AutoFixIssueResponse,
  type CancelAuditRequest,
  type CancelAuditResponse,
  type GetGroupedIssuesResponse,
  type RegenerateSummaryResponse,
  type DismissIssuesResponse,
  type DismissRecommendationResponse,
  type AutoFixStreamEvent,
  streamAutoFix,
  getAutoFixJobStatus,
} from '@/apis/observabilityAuditApi';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const observabilityAuditQueryKeys = {
  audits: (orgUuid: string) => ['observability-audits', orgUuid] as const,
  auditsForRepo: (orgUuid: string, repoName: string) =>
    ['observability-audits-for-repo', orgUuid, repoName] as const,
  auditStatus: (auditId: string, orgUuid: string) =>
    ['observability-audit-status', auditId, orgUuid] as const,
  assessment: (auditId: string, orgUuid: string) =>
    ['observability-audit-assessment', auditId, orgUuid] as const,
  coverage: (auditId: string, orgUuid: string) =>
    ['observability-audit-coverage', auditId, orgUuid] as const,
  issues: (
    auditId: string,
    orgUuid: string,
    filters?: Record<string, unknown>
  ) => ['observability-audit-issues', auditId, orgUuid, filters] as const,
  highLevelRecs: (auditId: string, orgUuid: string) =>
    ['observability-audit-recs', auditId, orgUuid] as const,
  groupedIssues: (auditId: string, orgUuid: string) =>
    ['observability-audit-grouped-issues', auditId, orgUuid] as const,
};

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export const useListAudits = () => {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  const { data, isLoading, error, refetch } = useQuery<ListAuditsResponse>({
    queryKey: observabilityAuditQueryKeys.audits(orgUuid),
    queryFn: async (): Promise<ListAuditsResponse> => {
      return await listObservabilityAudits({ org_uuid: orgUuid });
    },
    enabled: Boolean(orgUuid),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000,
  });

  return { data, isLoading, error, refetch };
};

export const useAuditStatus = (auditId: string | undefined) => {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useQuery<GetAuditStatusResponse>({
    queryKey: observabilityAuditQueryKeys.auditStatus(auditId ?? '', orgUuid),
    queryFn: async (): Promise<GetAuditStatusResponse> => {
      return await getObservabilityAuditStatus({
        org_uuid: orgUuid,
        audit_id: auditId!,
      });
    },
    enabled: Boolean(orgUuid && auditId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (
        data?.scan_status === 'completed' ||
        data?.scan_status === 'failed' ||
        data?.scan_status === 'cancelled'
      ) {
        return false;
      }
      return 5000;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error.message.includes('not found')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useAuditAssessment = (
  auditId: string | undefined,
  enabled?: boolean
) => {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useQuery<GetAuditAssessmentResponse>({
    queryKey: observabilityAuditQueryKeys.assessment(auditId ?? '', orgUuid),
    queryFn: async (): Promise<GetAuditAssessmentResponse> => {
      return await getObservabilityAuditAssessment({
        org_uuid: orgUuid,
        audit_id: auditId!,
      });
    },
    enabled: Boolean(orgUuid && auditId) && enabled !== false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000,
  });
};

export const useCoverageHeatmap = (
  auditId: string | undefined,
  enabled?: boolean
) => {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useQuery<GetCoverageHeatmapResponse>({
    queryKey: observabilityAuditQueryKeys.coverage(auditId ?? '', orgUuid),
    queryFn: async (): Promise<GetCoverageHeatmapResponse> => {
      return await getObservabilityCoverageHeatmap({
        org_uuid: orgUuid,
        audit_id: auditId!,
      });
    },
    enabled: Boolean(orgUuid && auditId) && enabled !== false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000,
  });
};

export const useAuditIssues = (
  auditId: string | undefined,
  filters?: {
    severity_filter?: string[];
    category_filter?: string[];
    page?: number;
    page_size?: number;
  }
) => {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useQuery<GetAuditIssuesResponse>({
    queryKey: observabilityAuditQueryKeys.issues(
      auditId ?? '',
      orgUuid,
      filters as Record<string, unknown>
    ),
    queryFn: async (): Promise<GetAuditIssuesResponse> => {
      return await getObservabilityAuditIssues({
        org_uuid: orgUuid,
        audit_id: auditId!,
        ...filters,
      });
    },
    enabled: Boolean(orgUuid && auditId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000,
  });
};

export const useHighLevelRecs = (
  auditId: string | undefined,
  enabled?: boolean
) => {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useQuery<GetHighLevelRecsResponse>({
    queryKey: observabilityAuditQueryKeys.highLevelRecs(auditId ?? '', orgUuid),
    queryFn: async (): Promise<GetHighLevelRecsResponse> => {
      return await getObservabilityHighLevelRecs({
        org_uuid: orgUuid,
        audit_id: auditId!,
      });
    },
    enabled: Boolean(orgUuid && auditId) && enabled !== false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000,
  });
};

export const useGroupedIssues = (
  auditId: string | undefined,
  enabled?: boolean
) => {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  const query = useQuery<GetGroupedIssuesResponse>({
    queryKey: observabilityAuditQueryKeys.groupedIssues(auditId ?? '', orgUuid),
    queryFn: async (): Promise<GetGroupedIssuesResponse> => {
      return await getGroupedIssues({
        org_uuid: orgUuid,
        audit_id: auditId!,
      });
    },
    enabled: Boolean(orgUuid && auditId) && enabled !== false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000,
  });

  return query;
};

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export const useStartAudit = () => {
  const queryClient = useQueryClient();
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useMutation<StartAuditResponse, Error, StartAuditRequest>({
    mutationFn: async (req: StartAuditRequest): Promise<StartAuditResponse> => {
      return await startObservabilityAudit(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.audits(orgUuid),
      });
      // Also invalidate all per-repo queries so the detail page
      // picks up the new in-progress audit immediately.
      queryClient.invalidateQueries({
        queryKey: ['observability-audits-for-repo', orgUuid],
      });
    },
  });
};

export const useCancelAudit = () => {
  const queryClient = useQueryClient();
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useMutation<CancelAuditResponse, Error, CancelAuditRequest>({
    mutationFn: async (
      req: CancelAuditRequest
    ): Promise<CancelAuditResponse> => {
      return await cancelObservabilityAudit(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.audits(orgUuid),
      });
      // Also invalidate per-repo queries so the detail page
      // reflects the cancelled status immediately.
      queryClient.invalidateQueries({
        queryKey: ['observability-audits-for-repo', orgUuid],
      });
    },
  });
};

export const useAutoFixIssue = () => {
  const queryClient = useQueryClient();
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useMutation<AutoFixIssueResponse, Error, AutoFixIssueRequest>({
    mutationFn: async (
      req: AutoFixIssueRequest
    ): Promise<AutoFixIssueResponse> => {
      return await autoFixObservabilityIssue(req);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.issues(
          variables.audit_id,
          orgUuid
        ),
      });
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.groupedIssues(
          variables.audit_id,
          orgUuid
        ),
      });
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.assessment(
          variables.audit_id,
          orgUuid
        ),
      });
    },
  });
};

export const useRegenerateSummary = () => {
  const queryClient = useQueryClient();
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useMutation<RegenerateSummaryResponse, Error, { audit_id: string }>({
    mutationFn: async (req): Promise<RegenerateSummaryResponse> => {
      return await regenerateObservabilitySummary({
        org_uuid: orgUuid,
        audit_id: req.audit_id,
      });
    },
    onSuccess: (_data, variables) => {
      // The call is synchronous — by the time we get here, the DB is updated.
      // Invalidate assessment + grouped issues to refetch the new data.
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.assessment(
          variables.audit_id,
          orgUuid
        ),
      });
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.groupedIssues(
          variables.audit_id,
          orgUuid
        ),
      });
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.audits(orgUuid),
      });
    },
  });
};

export const useDismissIssues = () => {
  const queryClient = useQueryClient();
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useMutation<
    DismissIssuesResponse,
    Error,
    { audit_id: string; issue_ids: string[] }
  >({
    mutationFn: async ({ audit_id, issue_ids }) => {
      return await dismissObservabilityIssues({
        org_uuid: orgUuid,
        audit_id,
        issue_ids,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.issues(
          variables.audit_id,
          orgUuid
        ),
      });
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.groupedIssues(
          variables.audit_id,
          orgUuid
        ),
      });
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.assessment(
          variables.audit_id,
          orgUuid
        ),
      });
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.audits(orgUuid),
      });
      queryClient.invalidateQueries({
        queryKey: ['observability-audits-for-repo', orgUuid],
      });
    },
  });
};

export const useDismissRecommendation = () => {
  const queryClient = useQueryClient();
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  return useMutation<
    DismissRecommendationResponse,
    Error,
    { audit_id: string; recommendation_title: string }
  >({
    mutationFn: async ({ audit_id, recommendation_title }) => {
      return await dismissObservabilityRecommendation({
        org_uuid: orgUuid,
        audit_id,
        recommendation_title,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: observabilityAuditQueryKeys.highLevelRecs(
          variables.audit_id,
          orgUuid
        ),
      });
    },
  });
};

// ---------------------------------------------------------------------------
// Repo list hook (complete org repo list for the overview page)
// ---------------------------------------------------------------------------

export const useAllOrgRepos = () => {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
  const userUuid = user?.UserUUID ?? '';

  return useQuery<Array<{ name: string; url: string; hasPRBot: boolean }>>({
    queryKey: ['observabilityAllRepos', orgUuid, userUuid],
    queryFn: async () => {
      // Walk the DB-backed listing to completion — no provider crawl, no
      // truncation (the old fetch-all capped at 200 rows per request).
      const pageSize = 200;
      const repos: Array<{ name: string; url: string; hasPRBot: boolean }> =
        [];
      for (let offset = 0; ; offset += pageSize) {
        const page = await listCodebaseRepos(orgUuid, userUuid, {
          limit: pageSize,
          offset,
        });
        repos.push(
          ...page.repositories.map((r) => ({
            name: r.name,
            url: r.url,
            hasPRBot: r.hasPRBot ?? false,
          }))
        );
        if (!page.has_more || page.repositories.length === 0) break;
      }
      return repos;
    },
    enabled: Boolean(orgUuid),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

// ---------------------------------------------------------------------------
// Helper hooks
// ---------------------------------------------------------------------------

export const useLatestAuditForRepo = (repoName: string | undefined) => {
  // Derive the latest run from `useAllAuditsForRepo` so both hooks share the
  // same non-truncated, repo-scoped query. Previously this reused the org-wide
  // `useListAudits` (limit 20), so for orgs with >20 audits the latest
  // per-repo run could be missing — and since the detail page displays this
  // value by default, the truncation affected what the user saw.
  const { audits, isLoading } = useAllAuditsForRepo(repoName);

  return { audit: audits[0], isLoading };
};

/**
 * Filters a list of audit summaries down to those belonging to `repoName`,
 * matching either the bare name or an `org/repo` form (case-insensitive),
 * then sorts them newest-first by `created_at`. Pure function — exported so
 * the filtering/sorting logic can be unit-tested without rendering hooks.
 */
export function filterAuditsForRepo(
  audits: AuditSummary[] | undefined,
  repoName: string | undefined
): AuditSummary[] {
  if (!audits || !repoName) return [];

  const repoNameLower = repoName.toLowerCase();
  return audits
    .filter(
      (a) =>
        a.repo_name.toLowerCase() === repoNameLower ||
        a.repo_name.toLowerCase().endsWith(`/${repoNameLower}`)
    )
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
}

/**
 * Returns every historical audit run for a single repo, newest first.
 *
 * Previously this reused the shared `useListAudits` query, which fetches
 * org-wide with the default page size (limit=20). For orgs with more than 20
 * audits, older per-repo runs were silently truncated.
 *
 * The backend `list_audits` endpoint supports server-side `repo_name`
 * filtering and a `limit` (max 100). For canonical `owner/repo` names — the
 * form the backend stores and matches exactly — we pass `repo_name` so the
 * result set is narrowed to this repo server-side and the page size is raised
 * to 100, eliminating the org-wide truncation. Bare repo names (legacy
 * deep-link form, e.g. from the home card) are not forwarded because the
 * backend's exact match would miss audits stored under `owner/repo`; those are
 * fetched org-wide (limit 100) and matched client-side by `filterAuditsForRepo`,
 * which handles both name forms. `filterAuditsForRepo` is applied in both
 * branches as a defensive filter.
 */
export const useAllAuditsForRepo = (repoName: string | undefined) => {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';

  // The backend matches `repo_name` exactly and stores the `owner/repo` form.
  // Only forward canonical names; bare names are matched client-side below.
  const isCanonicalRepoName = Boolean(repoName && repoName.includes('/'));

  const { data, isLoading, error } = useQuery<ListAuditsResponse>({
    queryKey: observabilityAuditQueryKeys.auditsForRepo(orgUuid, repoName ?? ''),
    queryFn: async (): Promise<ListAuditsResponse> => {
      return await listObservabilityAudits({
        org_uuid: orgUuid,
        ...(isCanonicalRepoName ? { repo_name: repoName } : {}),
        limit: 100,
      });
    },
    enabled: Boolean(orgUuid && repoName),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000,
  });

  const audits = useMemo(
    () => filterAuditsForRepo(data?.audits, repoName),
    [data?.audits, repoName]
  );

  return { audits, isLoading, error };
};

// ---------------------------------------------------------------------------
// Auto-fix SSE stream hook
// ---------------------------------------------------------------------------

export type AutoFixStreamStatus = 'idle' | 'streaming' | 'complete' | 'error';

const STORAGE_KEY_PREFIX = 'autofix_job_';

function getStoredJobId(auditId: string): string | null {
  try {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}${auditId}`);
  } catch {
    return null;
  }
}

function storeJobId(auditId: string, jobId: string): void {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${auditId}`, jobId);
  } catch {
    // localStorage unavailable
  }
}

function clearJobId(auditId: string): void {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${auditId}`);
  } catch {
    // localStorage unavailable
  }
}

export interface UseAutoFixStreamReturn {
  status: AutoFixStreamStatus;
  currentEvent: AutoFixStreamEvent | null;
  fixesGenerated: number;
  start: (issueIds: string[]) => void;
  cancel: () => void;
}

export const useAutoFixStream = (
  auditId: string,
  orgUuid: string,
  /** Optional key to scope localStorage per caller (e.g. workstream title). Defaults to auditId. */
  streamKey?: string
): UseAutoFixStreamReturn => {
  const storageKey = streamKey ? `${auditId}_${streamKey}` : auditId;
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AutoFixStreamStatus>('idle');
  const [currentEvent, setCurrentEvent] = useState<AutoFixStreamEvent | null>(
    null
  );
  const [fixesGenerated, setFixesGenerated] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: observabilityAuditQueryKeys.issues(auditId, orgUuid),
    });
    queryClient.invalidateQueries({
      queryKey: observabilityAuditQueryKeys.groupedIssues(auditId, orgUuid),
    });
    queryClient.invalidateQueries({
      queryKey: observabilityAuditQueryKeys.assessment(auditId, orgUuid),
    });
  }, [auditId, orgUuid, queryClient]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Poll the lightweight status endpoint as fallback
  const pollFailCountRef = useRef(0);
  const MAX_POLL_FAILURES = 36; // 36 × 5s = 3 minutes of consecutive failures

  const startPolling = useCallback(
    (jobId: string) => {
      stopPolling();
      pollFailCountRef.current = 0;
      setStatus('streaming');

      const poll = async () => {
        try {
          const jobStatus = await getAutoFixJobStatus(jobId);
          pollFailCountRef.current = 0; // Reset on success

          setCurrentEvent({
            stage:
              (jobStatus.stage as AutoFixStreamEvent['stage']) ?? 'started',
            message: jobStatus.message ?? 'Fixing issues...',
            total_issues: jobStatus.total_issues,
            fixed: jobStatus.fixed,
            failed: jobStatus.failed,
            pr_url: jobStatus.pr_url,
            is_complete: jobStatus.is_complete,
          });

          if (!jobStatus.active || jobStatus.is_complete) {
            stopPolling();
            clearJobId(storageKey);
            // Job not found / expired → reset silently; actual completion has a stage
            if (!jobStatus.stage) {
              setStatus('idle');
            } else {
              setStatus(jobStatus.stage === 'error' ? 'error' : 'complete');
              invalidateQueries();
            }
          }
        } catch {
          pollFailCountRef.current += 1;
          if (pollFailCountRef.current >= MAX_POLL_FAILURES) {
            stopPolling();
            clearJobId(storageKey);
            setStatus('error');
            setCurrentEvent({
              stage: 'error',
              message: 'Lost connection to the fix process. Please try again.',
              is_complete: true,
            });
          }
        }
      };

      // Poll immediately, then every 5s
      poll();
      pollRef.current = setInterval(poll, 5_000);
    },
    [invalidateQueries, stopPolling, storageKey]
  );

  // On mount: check localStorage for an active job and resume polling
  useEffect(() => {
    const storedJobId = getStoredJobId(storageKey);
    if (storedJobId) {
      startPolling(storedJobId);
    }

    return () => {
      stopPolling();
      abortRef.current?.abort();
      abortRef.current = null;
    };
    // Only run on mount / when storageKey changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const start = useCallback(
    (issueIds: string[]) => {
      // Cancel any existing stream/poll
      abortRef.current?.abort();
      stopPolling();

      const controller = new AbortController();
      abortRef.current = controller;

      setStatus('streaming');
      setCurrentEvent(null);
      setFixesGenerated(0);

      streamAutoFix(
        {
          org_uuid: orgUuid,
          audit_id: auditId,
          issue_ids: issueIds,
        },
        {
          onEvent: (event) => {
            setCurrentEvent(event);

            // Store job_id from the first event
            if (event.job_id) {
              storeJobId(storageKey, event.job_id);
            }

            if (event.stage === 'fix_generated') {
              setFixesGenerated((prev) => prev + 1);
            }

            if (event.stage === 'error') {
              setStatus('error');
              clearJobId(storageKey);
            }
          },
          onError: () => {
            // SSE broke — fall back to polling if we have a job_id
            const jobId = getStoredJobId(storageKey);
            if (jobId) {
              startPolling(jobId);
            } else {
              setStatus('error');
            }
          },
          onComplete: () => {
            setStatus('complete');
            clearJobId(storageKey);
            invalidateQueries();
          },
        },
        controller.signal
      ).catch(() => {
        // stream errors handled in callbacks
      });
    },
    [auditId, orgUuid, invalidateQueries, stopPolling, startPolling, storageKey]
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    stopPolling();
    clearJobId(storageKey);
    setStatus('idle');
    setCurrentEvent(null);
  }, [stopPolling, storageKey]);

  return { status, currentEvent, fixesGenerated, start, cancel };
};

// ---------------------------------------------------------------------------
// Provider helpers for observability audit mutations
// ---------------------------------------------------------------------------

/**
 * Returns true for any GitLab provider (gitlab.com or self-hosted).
 * Self-hosted GitLab sessions use the org's organization_name as the provider
 * ID (not the literal "gitlab-self-hosted"), so any non-"github" provider is
 * treated as GitLab.
 */
export function isGitLabProvider(provider?: string): boolean {
  return !!provider && provider !== 'github';
}

/**
 * Strips the scheme from a URL so the backend can safely embed it in a clone
 * URL. The observability backend builds clone URLs as:
 *   https://oauth2:{token}@{repo_host}/{repo}.git
 * so repo_host must be a bare hostname (e.g. "git.mycorp.com"), not a full URL.
 */
export function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

/**
 * Hook that returns the repo_provider and repo_host values to pass to the
 * observability audit start mutation. Normalises self-hosted GitLab provider
 * IDs to "gitlab" and strips the URL scheme from the host so the backend
 * receives a bare hostname.
 */
export function useRepoProviderInfo(): {
  repo_provider: string;
  repo_host: string;
} {
  const { user } = useUserLogged();
  const getAuthUrl = useAuthStore((s) => s.getAuthUrl);
  const provider = user?.provider;
  const repoProvider = isGitLabProvider(provider) ? 'gitlab' : 'github';
  const rawHost = getAuthUrl(provider);
  const repoHost = rawHost ? stripScheme(rawHost) : '';
  return { repo_provider: repoProvider, repo_host: repoHost };
}
